/**
 * The custom-headers editor for tool definitions and webhook endpoints — the
 * OriginsEditor pattern (config-editor.tsx) with a second column, shared by the
 * Tools and Webhooks pages so the two cannot drift.
 *
 * WHAT MAKES THIS EDITOR UNLIKE EVERY OTHER FORM IN THE PANEL: THE VALUES ARE
 * WRITE-ONLY. A header value is a credential for the customer's own endpoint
 * (`Authorization: Bearer …` is the expected case), so the server never returns
 * one — reads carry `headerNames` only. That single fact shapes everything here:
 *
 *   - A row for a stored header renders its NAME and an empty value box with
 *     the placeholder "(unchanged)", because the browser does not have the
 *     value and must not pretend to.
 *
 *   - AN UNTOUCHED EDITOR SENDS NOTHING. The PATCH contract is omitted =
 *     preserve, `{}` = clear, and anything else REPLACES THE WHOLE MAP — the
 *     server has no per-key merge, deliberately, because a merge would need a
 *     per-key delete syntax and that is a second protocol. So the only way to
 *     leave stored values alone is to omit the field, which is what an
 *     untouched editor does.
 *
 *   - THE MOMENT ANYTHING IS TOUCHED, EVERY SURVIVING ROW NEEDS A TYPED VALUE.
 *     This is the consequence of whole-map replacement that a hasty reading of
 *     "untouched rows send nothing" gets wrong: sending only the edited rows
 *     would replace the map and silently DELETE the untouched ones — a
 *     credential wiped by a form that looked like it preserved it. Rejected
 *     rather than normalised, per the house rule: a "(unchanged)" row in a
 *     touched editor is a per-row error telling the reader to retype the value
 *     or remove the row, and Save stays unavailable until they do.
 *
 *   - REMOVING EVERY ROW IS NOT ENOUGH TO CLEAR. An empty list in a touched
 *     editor could mean "delete my stored credentials" or "I fumbled the rows
 *     and want out", and the two must not share an outcome. Clearing requires
 *     ticking the explicit confirmation this component renders for exactly that
 *     state, which is what makes the `{}` PATCH a decision rather than an
 *     accident.
 *
 * Validation mirrors the server (workers/panel/src/tools.ts `readHeaders`):
 * token-charset names ≤128 chars, no case-insensitive duplicates, non-empty
 * values ≤2048 chars with no control characters. Mirrored so the reader learns
 * about a bad row while typing it rather than from a 400 — the server remains
 * the authority, and `messageFor` on each page reports anything it refuses that
 * this copy missed.
 */
import { PlusIcon, XIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

/** Same charset and cap as the panel worker's HEADER_NAME_RE. */
const HEADER_NAME_RE = /^[a-zA-Z0-9!#$%&'*+.^_`|~-]{1,128}$/

export interface HeaderRow {
  name: string
  value: string
  /**
   * True when this row stands for a header the server already stores. Its value
   * box starts empty NOT because the value is empty but because this browser
   * has never seen it — see the file header.
   */
  fromServer: boolean
}

/** The rows an edit form starts from: one value-less row per stored name. */
export function seedHeaderRows(storedNames: readonly string[]): HeaderRow[] {
  return storedNames.map((name) => ({ name, value: '', fromServer: true }))
}

/**
 * Has the reader touched the headers at all? Compared against the seed rather
 * than tracked with a flag, so Discard-by-hand — retyping a row back to what it
 * was — genuinely un-dirties the editor. A `fromServer` row is untouched only
 * while its value is still empty AND its name is still the stored one.
 */
export function headerRowsDirty(rows: readonly HeaderRow[], storedNames: readonly string[]): boolean {
  if (rows.length !== storedNames.length) return true
  return rows.some(
    (r, i) => !r.fromServer || r.value !== '' || r.name !== storedNames[i],
  )
}

/**
 * What is wrong with row `i`, or null. `touched` is the editor-wide dirty flag:
 * an empty value on a `fromServer` row is only an error once something changed,
 * because until then the row is the "preserve what is stored" state itself.
 */
export function headerRowProblem(
  rows: readonly HeaderRow[],
  i: number,
  touched: boolean,
): string | null {
  const row = rows[i]
  if (!row) return null
  const name = row.name.trim()
  if (name === '') return 'Empty name. Type a header name or remove the row.'
  if (!HEADER_NAME_RE.test(name)) {
    return 'Not a valid HTTP header name: letters, digits and !#$%&\'*+.^_`|~- only, up to 128 characters, no spaces.'
  }
  const lower = name.toLowerCase()
  const first = rows.findIndex((r) => r.name.trim().toLowerCase() === lower)
  if (first !== i) {
    return 'This name is already on another row. Header names are case-insensitive on the wire, so only one value would be sent — keep one.'
  }
  if (row.value === '') {
    if (!row.fromServer) return 'Empty value. Type the value or remove the row.'
    if (touched) {
      return 'The stored value is write-only, so this browser cannot re-send it — and this save replaces the whole set. Type the value again to keep this header, or remove the row to drop it.'
    }
    return null
  }
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1f\x7f]/.test(row.value)) {
    return 'The value contains control characters, which the server refuses — a newline in a header is request splitting.'
  }
  if (row.value.length > 2048) return 'The value exceeds 2048 characters.'
  return null
}

/** True when every row would survive the server's own checks. */
export function headerRowsClean(rows: readonly HeaderRow[], touched: boolean): boolean {
  return rows.every((_, i) => headerRowProblem(rows, i, touched) === null)
}

/** The full map a touched, clean editor sends. Never call it on an untouched
 *  editor — omitting the field is how stored values are preserved. */
export function buildHeaders(rows: readonly HeaderRow[]): Record<string, string> {
  const out: Record<string, string> = {}
  for (const r of rows) out[r.name.trim()] = r.value
  return out
}

export function HeadersEditor({
  idPrefix,
  rows,
  onChange,
  storedNames = [],
  clearConfirmed = false,
  onClearConfirmed,
}: {
  /** Keeps input ids unique when two of these are on one screen. */
  idPrefix: string
  rows: HeaderRow[]
  /**
   * An updater, not a finished list — the OriginsEditor lesson, kept: several
   * presses of "Add a header" inside one React batch would otherwise all derive
   * from the same base list and add one row between them.
   */
  onChange: (update: (prev: HeaderRow[]) => HeaderRow[]) => void
  /** The names the server holds for the row being edited. Empty on a create form. */
  storedNames?: readonly string[]
  /** The explicit "yes, clear the stored set" tick. See the file header. */
  clearConfirmed?: boolean
  onClearConfirmed?: (on: boolean) => void
}) {
  const touched = headerRowsDirty(rows, storedNames)
  const id = (n: number, half: 'name' | 'value') => `${idPrefix}-header-${n}-${half}`
  // The one state that needs the explicit confirmation: stored headers exist,
  // and the reader has deleted every row.
  const clearing = touched && rows.length === 0 && storedNames.length > 0

  return (
    <div className="field">
      <Label>Custom headers</Label>
      <p className="text-xs text-muted-foreground">
        Sent verbatim with every request to this URL — the place for an{' '}
        <span className="mono">Authorization</span> value. Values are write-only: after
        saving, the panel shows the names and never the values again.
      </p>

      {rows.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {rows.map((row, i) => {
            const problem = headerRowProblem(rows, i, touched)
            return (
              <div key={i} className="flex flex-col gap-1">
                <div className="flex items-center gap-1.5">
                  <Input
                    id={id(i, 'name')}
                    value={row.name}
                    aria-invalid={problem !== null}
                    aria-label={`Header ${i + 1} name`}
                    placeholder="Authorization"
                    className="mono max-w-56"
                    // A stored row keeps its name fixed: renaming it cannot carry
                    // the value along (the browser does not have the value), so a
                    // "rename" is really remove-and-add, and the two-control
                    // version of that says so instead of implying a move.
                    disabled={row.fromServer}
                    onChange={(e) =>
                      onChange((prev) =>
                        prev.map((r, j) => (j === i ? { ...r, name: e.target.value } : r)),
                      )
                    }
                  />
                  <Input
                    id={id(i, 'value')}
                    value={row.value}
                    aria-invalid={problem !== null}
                    aria-label={`Header ${i + 1} value`}
                    placeholder={row.fromServer ? '(unchanged)' : 'value'}
                    className="mono"
                    onChange={(e) =>
                      onChange((prev) =>
                        prev.map((r, j) => (j === i ? { ...r, value: e.target.value } : r)),
                      )
                    }
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    title="Remove this header"
                    aria-label={`Remove header ${i + 1}`}
                    onClick={() => onChange((prev) => prev.filter((_, j) => j !== i))}
                  >
                    <XIcon />
                  </Button>
                </div>
                {problem && <p className="text-xs text-ev-bad">{problem}</p>}
              </div>
            )
          })}
        </div>
      )}

      {clearing && (
        <div className="rounded-md border border-ev-warn-border bg-ev-warn-bg px-3 py-2 text-xs text-ev-warn">
          <p>
            <strong className="font-semibold">
              Every stored header has been removed from this form.
            </strong>{' '}
            Saving like this deletes {storedNames.length === 1 ? 'the stored value' : 'all of them'}{' '}
            from the server — and the panel cannot show them back to you afterwards, because it
            never sees the values. Tick to confirm, or Discard.
          </p>
          {onClearConfirmed && (
            <label className="mt-1.5 flex cursor-pointer items-center gap-2 font-medium">
              <Checkbox
                checked={clearConfirmed}
                onCheckedChange={(v) => onClearConfirmed(v === true)}
              />
              Clear the stored headers
            </label>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="xs"
          onClick={() => onChange((prev) => [...prev, { name: '', value: '', fromServer: false }])}
        >
          <PlusIcon /> Add a header
        </Button>
        {touched && storedNames.length > 0 && rows.length > 0 && (
          <span className="text-xs text-muted-foreground">
            {/* Said here, at the moment it starts being true, not only in a 400:
                the save replaces the whole set, so a row still reading
                "(unchanged)" now needs its value typed again. */}
            Changing anything re-sends the whole set — rows still marked (unchanged) need
            their value typed again or the row removed.
          </span>
        )}
      </div>
    </div>
  )
}
